<template>
    <v-dialog v-model="dialog" lazy fullscreen transition="dialog-bottom-transition">
        <v-toolbar fixed dark color="teal darken-1" class="absolute top pa-2 lighten-3">

            <template v-if="loading">
                <v-progress-linear indeterminate :height="2" color="white"/>
            </template>

            <template v-if="client">
                <v-btn icon @click="hide">
                    <v-icon>close</v-icon>
                </v-btn>
                <v-layout wrap>
                    <v-flex xs12>
                        <h3 class="title font-weight-light text-xs-center grow">
                            {{ client.first_name }} {{ client.last_name || '' }}
                        </h3>
                    </v-flex>
                    <v-flex xs12 text-xs-center>Historial Clínico</v-flex>
                </v-layout>
                <v-avatar>
                    <v-img :src="client.avatar || getFakeAvatar(client.gender)"></v-img>
                </v-avatar>
            </template>

        </v-toolbar>
        <v-card
            class="mx-auto"
            max-width="400"
        >
            <v-container>
                <v-layout wrap>
                    <v-flex xs12 md10 offset-md1 lg8 offset-lg2 xl6 offset-xl3>
                        <v-slide-y-transition>
                            <div v-show="!loading">

                                <v-card-text class="py-0 mt-4 pt-4">
                                    <v-timeline
                                        align-top
                                        dense
                                    >
                                        <div class="mt-4 text-xs-center">
                                            <div v-if="history.length === 0">Aún no registra citas</div>
                                        </div>
                                        <template v-for="(yearGroup) in groups">
                                            <h1>{{ yearGroup.year }}</h1>
                                            <template v-for="(item, i) in yearGroup.history">
                                                <client-attention-history-item @click="showSummary" :item="item"/>
                                            </template>
                                        </template>

                                    </v-timeline>
                                </v-card-text>
                            </div>
                        </v-slide-y-transition>
                    </v-flex>
                </v-layout>

            </v-container>
            <attention-summary-dialog ref="attentionSummaryDialog" @click:event-load="hideAll"/>
        </v-card>
    </v-dialog>
</template>

<script>
import {getFakeAvatar} from '@/utils/Common'
import {getCompanionLabel, getSubTypeLabel, getSubTypeIcon} from '@/utils/ScheduleEvent'
import {ApiUtils} from '@/utils/ApiUtils'
import {mapState, mapMutations} from 'vuex'
import AttentionSummaryDialog from './AttentionSummaryDialog'
import ClientAttentionHistoryItem from "../ClientAttentionHistoryItem";

export default {
    components: {ClientAttentionHistoryItem, AttentionSummaryDialog},
    data() {
        return {
            dialog: false,
            loading: false,
            history: [],
            client: null,
            total: 0
        }
    },
    methods: {
        ...mapMutations(['showSnackBar']),
        getFakeAvatar,
        hideAll() {
            const vm = this
            vm.$refs.attentionSummaryDialog.hide()
            vm.hide()
        },
        showSummary(evtId) {
            var vm = this
            const evt = vm.history.find(evt => evt.id === evtId)
            vm.$refs.attentionSummaryDialog.show(evt)
        },
        async show(clientId) {
            var vm = this

            vm.client = null
            vm.history = []
            vm.dialog = true

            if (vm.loading || !vm.currentProvider) return
            vm.loading = true
            const response = await ApiUtils.makeGet(
                `providers/${vm.currentProvider.id}/client/${clientId}/attention-history`
            )
            vm.loading = false

            if (!response.ok) {
                const json = await response.json()
                vm.showSnackBar(json.message || 'Ha ocurrido un error cargando el historial')
                return
            }

            const json = await response.json()
            vm.history = json.result.attention_history.data
            vm.total = json.result.attention_history.total
            vm.client = json.result.client

        },
        hide() {
            this.dialog = false
        }
    },
    computed: {
        groups() {
            var vm = this
            if (vm.history.length === 0) return []
            const years = vm.history.map(
                evt => vm.$moment(evt.date, 'YYYY').format('YYYY')
            )
            const groups = [...new Set(years)]
                .sort((a, b) => b - a)

            return groups
                .map(year => (
                    {
                        year,
                        history: vm.history
                            .filter(evt => {
                                return vm.$moment(evt.date, 'YYYY').format('YYYY') === year
                            }).map(event => {
                                var evt = JSON.parse(JSON.stringify(event))
                                evt.human_date_time = vm.$moment(evt.date).format('ddd') + ', ' +
                                    vm.$moment(evt.date).format('D') + ' ' +
                                    vm.$moment(evt.date).format('MMMM')
                                evt.time = vm.$moment(evt.time, 'HH:mm:ss').format('HH:mm') + ' hrs.'

                                if (!evt || !evt.anamnesis ||
                                    (
                                        evt.anamnesis && evt.anamnesis.symptoms.length === 0 &&
                                        evt.anamnesis.anamnesis_options.length === 0 &&
                                        !evt.anamnesis.comment
                                    )

                                ) {
                                    evt.anamnesis = null
                                } else {
                                    var anamnesis = JSON.parse(JSON.stringify(evt.anamnesis))
                                    anamnesis.symptoms = anamnesis.symptoms.map(symptom => {
                                        const eye = symptom.pivot.eye ? (
                                            symptom.pivot.eye === 'left' ? 'izquierdo' :
                                                (symptom.pivot.eye === 'right' ? 'derecho' : 'ambos')
                                        ) : null
                                        return {
                                            description: symptom.description,
                                            duration: symptom.pivot.duration,
                                            duration_unit: vm.durationUnits[symptom.pivot.duration_unit],
                                            eye: eye
                                        }
                                    })

                                    evt.anamnesis = anamnesis
                                }

                                if (evt.client_relationship) {
                                    evt.relationship = getCompanionLabel(evt.client_relationship)
                                } else {
                                    evt.relationship = null
                                }

                                evt.sub_type_description = getSubTypeLabel(evt.sub_type)
                                evt.sub_type_icon = getSubTypeIcon(evt.sub_type)

                                if (evt.psychological_record) {
                                    evt.discharge = evt.psychological_record.last_session && evt.psychological_record.session_number !== 0
                                    evt.session_name = evt.psychological_record.session_number === 0 ? 'Primera cita' :
                                        `Sesión ${evt.psychological_record.session_number}`
                                    evt.session_color = evt.psychological_record.session_number === 0 ? 'secondary' : 'primary'
                                }

                                return evt
                            })
                    }
                ))

        },
        durationUnits() {
            return {
                'year': 'año(s)',
                'month': 'mes(es)',
                'week': 'semana(s)',
                'day': 'día(s)',
                'hour': 'hora(s)'
            }
        },
    }
}
</script>

<style scoped>

.indent {
    margin-left: 0
}

.total {
    font-family: Roboto, sans-serif;
    font-size: 3em;
    font-weight: 300;
}
</style>